import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import { useSession } from '../Utils/SessionContext';
import toast from 'react-hot-toast';
import ChallengeQuestionCard from './Shards/Questions/ChallengeQuestionCard';
import EditChallengeQuestion from './Shards/Questions/EditChallengeQuestion';
import ConfirmationPopup from '../Utils/ConfirmationPopup';
import { updateChallengeQuestions } from './Utils/QuestionUpdater';

const ChallengeQuestionList = ({ 
  questions, 
  onRegenerateQuestion, 
  showAnswers = false,
  challengeId = null,
  onQuestionUpdated = null,
  readOnly = false,
  onAddToChallenge = null
}) => {
  const { user } = useSession();

  // Local state for optimistic updates
  const [localQuestions, setLocalQuestions] = useState(questions);
  
  // Sync local state when props change
  useEffect(() => {
    setLocalQuestions(questions);
  }, [questions]);

  // State for edit modal
  const [editModalState, setEditModalState] = useState({
    isOpen: false,
    question: null,
    questionIndex: -1
  });

  // State for delete confirmation
  const [deleteModalState, setDeleteModalState] = useState({
    show: false,
    question: null,
    questionIndex: -1
  });

  // State for like confirmation
  const [likeModalState, setLikeModalState] = useState({
    show: false,
    questionId: null
  });

  // Handle opening edit modal
  const handleEditQuestion = (question, index) => {
    setEditModalState({
      isOpen: true,
      question: question,
      questionIndex: index
    });
  };

  // Handle closing edit modal
  const handleCloseEditModal = () => {
    setEditModalState({
      isOpen: false,
      question: null,
      questionIndex: -1
    });
  };

  // Handle successful question update
  const handleQuestionUpdateSuccess = () => {
    if (onQuestionUpdated) {
      onQuestionUpdated();
    } else {
      // Default: reload page
      window.location.reload();
    }
  };

  // Handle opening delete confirmation
  const handleDeleteQuestion = (question, index) => {
    setDeleteModalState({
      show: true,
      question: question,
      questionIndex: index
    });
  };

  // Handle canceling delete
  const handleCancelDelete = () => {
    setDeleteModalState({
      show: false,
      question: null,
      questionIndex: -1
    });
  };

  // Handle confirmed delete
  const handleConfirmDelete = async () => {
    const { questionIndex } = deleteModalState;
    
    try {
      // Create updated questions array without the deleted question
      const updatedQuestions = questions.filter((_, i) => i !== questionIndex);
      
      // Use utility function to update questions
      await updateChallengeQuestions(challengeId, updatedQuestions, user);

      toast.success('Question deleted successfully!');
      
      // Close modal
      handleCancelDelete();
      
      // Trigger refresh
      if (onQuestionUpdated) {
        onQuestionUpdated();
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error deleting question:', error);
      toast.error('Failed to delete question. Please try again.');
    }
  };

  // Handle opening like confirmation
  const handleLikeQuestion = (questionId) => {
    setLikeModalState({
      show: true,
      questionId: questionId
    });
  };

  // Handle canceling like
  const handleCancelLike = () => {
    setLikeModalState({
      show: false,
      questionId: null
    });
  };

  // Function to actually save question to library
  const handleConfirmLike = async () => {
    const { questionId } = likeModalState;
    console.log(`Liking question with ID: ${questionId}`);
    
    // Find the full question object
    const question = questions.find(q => q.id === questionId);
    if (!question) {
      console.error('Question not found:', questionId);
      toast.error('Question not found');
      return;
    }

    if (!user?.access_token) {
      toast.error('Authentication required. Please log in again.');
      return;
    }

    try {
      const requestBody = {
        item_type: question.type,
        item_category: "challenge",
        item_author: user?.id,
        question: question // Send the full question JSON
      };

      const response = await fetch(API_ENDPOINTS.LIB_ADD_ITEM, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.access_token}`
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const error = new Error(`Error: ${response.status} ${response.statusText}`);
        error.status = response.status;
        throw error;
      }

      const result = await response.json();
      console.log('Question added to library successfully:', result);
      toast.success('Question added to library!');
      
      // Close modal
      handleCancelLike();
    } catch (error) {
      console.error('Error adding question to library:', error);
      
      if (error.status === 401) {
        toast.error('Session expired. Please log in again.');
        return;
      }
      
      toast.error('Error adding question to library. Please try again.');
    }
  };

  // Handle moving a question up with optimistic update
  const handleMoveUp = (index) => {
    if (index === 0) return; // Can't move first question up

    // Store original for potential rollback
    const originalQuestions = [...localQuestions];
    
    // 1. INSTANT UI UPDATE (optimistic)
    const updatedQuestions = [...localQuestions];
    [updatedQuestions[index - 1], updatedQuestions[index]] = 
      [updatedQuestions[index], updatedQuestions[index - 1]];
    
    setLocalQuestions(updatedQuestions); // Update UI immediately
    
    // 2. BACKGROUND SERVER UPDATE (no await!)
    updateChallengeQuestions(challengeId, updatedQuestions, user)
      .then(() => {
        // Success - silent or subtle feedback
        console.log('Question moved up successfully');
      })
      .catch((error) => {
        // Error - rollback to original
        console.error('Error moving question up:', error);
        setLocalQuestions(originalQuestions); // Rollback
        toast.error('Failed to move question. Please try again.');
      });
  };

  // Handle moving a question down with optimistic update
  const handleMoveDown = (index) => {
    if (index === localQuestions.length - 1) return; // Can't move last question down

    // Store original for potential rollback
    const originalQuestions = [...localQuestions];
    
    // 1. INSTANT UI UPDATE (optimistic)
    const updatedQuestions = [...localQuestions];
    [updatedQuestions[index], updatedQuestions[index + 1]] = 
      [updatedQuestions[index + 1], updatedQuestions[index]];
    
    setLocalQuestions(updatedQuestions); // Update UI immediately
    
    // 2. BACKGROUND SERVER UPDATE (no await!)
    updateChallengeQuestions(challengeId, updatedQuestions, user)
      .then(() => {
        // Success - silent or subtle feedback
        console.log('Question moved down successfully');
      })
      .catch((error) => {
        // Error - rollback to original
        console.error('Error moving question down:', error);
        setLocalQuestions(originalQuestions); // Rollback
        toast.error('Failed to move question. Please try again.');
      });
  };

  if (!questions || questions.length === 0) {
    return (
      <div className="p-4 bg-gray-50 rounded-lg">
        <p className="text-gray-600">No questions available.</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {localQuestions.map((question, index) => (
          <ChallengeQuestionCard
            key={question.id}
            question={question}
            index={index}
            showAnswers={showAnswers}
            onRegenerateQuestion={onRegenerateQuestion}
            onLikeQuestion={handleLikeQuestion}
            onEditQuestion={handleEditQuestion}
            onDeleteQuestion={handleDeleteQuestion}
            onMoveUp={handleMoveUp}
            onMoveDown={handleMoveDown}
            totalQuestions={localQuestions.length}
            challengeId={challengeId}
            readOnly={readOnly}
            onAddToChallenge={onAddToChallenge}
          />
        ))}
      </div>
      
      {/* Edit Challenge Question Modal */}
      <EditChallengeQuestion
        isOpen={editModalState.isOpen}
        onClose={handleCloseEditModal}
        question={editModalState.question}
        questionIndex={editModalState.questionIndex}
        challengeId={challengeId}
        allQuestions={localQuestions}
        onSuccess={handleQuestionUpdateSuccess}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationPopup
        show={deleteModalState.show}
        title="Delete Question"
        message={`Are you sure you want to delete question #${deleteModalState.questionIndex + 1}? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {/* Like Confirmation Modal */}
      <ConfirmationPopup
        show={likeModalState.show}
        title="Save to Library"
        message="Are you sure you want to save this question in the library?"
        onConfirm={handleConfirmLike}
        onCancel={handleCancelLike}
      />
    </>
  );
};

export default ChallengeQuestionList;
